// src/services/organizationService.js

import apiClient from '../api/apiClient';
// Removing unused import: import { getOrgId } from '../utils/orgUtils';

/**
 * Get information about an organization by ID
 * @param {number} orgId - The organization ID
 * @returns {Promise<Object>} - Promise resolving to organization data
 */
export const getOrganizationById = async (orgId) => {
  try {
    const response = await apiClient.get(`/organizations/${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching organization:', error);
    throw error;
  }
};

/**
 * Update organization meta information
 * @param {number} orgId - The organization ID
 * @param {Object} metaData - The meta data to update
 * @returns {Promise<Object>} - Promise resolving to updated organization data
 */
export const updateOrganizationMeta = async (orgId, metaData) => {
  try {
    const response = await apiClient.put(`/organizations/${orgId}/meta`, { meta: metaData });
    return response.data;
  } catch (error) {
    console.error('Error updating organization meta:', error);
    throw error;
  }
};

/**
 * Update organization name
 * @param {number} orgId - The organization ID
 * @param {string} name - The new name for the organization
 * @returns {Promise<Object>} - Promise resolving to the update result
 */
export const updateOrganizationName = async (orgId, name) => {
  try {
    const response = await apiClient.put(`/organizations/${orgId}/name`, { name });
    return response.data;
  } catch (error) {
    console.error('Error updating organization name:', error);
    throw error;
  }
};

/**
 * Get all organizations for the current user
 * @returns {Promise<Array>} Promise resolving to the list of organizations.
 */
export const getOrganizations = async () => {
  try {
    const response = await apiClient.get('/organizations');
    return response.data;
  } catch (error) {
    console.error('Error fetching organizations:', error);
    throw error;
  }
};

/**
 * Create a new organization
 * @param {string} name - The name of the organization
 * @returns {Promise<Object>} Promise resolving to the created organization data
 */
export const createOrganization = async (name) => {
  try {
    const response = await apiClient.post('/organizations/create', { name });
    return response.data;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

/**
 * Invite a user to an organization
 * @param {number} orgId - The organization ID
 * @param {string} email - The email of the user to invite
 * @param {string} roleName - The role to assign to the invited user
 * @returns {Promise<Object>} Promise resolving to the invitation result
 */
export const inviteUserToOrganization = async (orgId, email, roleName) => {
  try {
    const response = await apiClient.post(`/organizations/${orgId}/invite`, {
      email,
      roleName,
    });
    return response.data;
  } catch (error) {
    console.error('Error inviting user to organization:', error);
    throw error;
  }
};

/**
 * Accept an invitation to an organization
 * @param {number} orgId - The organization ID
 * @returns {Promise<Object>} Promise resolving to the acceptance result
 */
export const acceptOrganizationInvite = async (orgId) => {
  try {
    const response = await apiClient.put(`/organizations/${orgId}/accept-invite`);
    return response.data;
  } catch (error) {
    console.error('Error accepting organization invite:', error);
    throw error;
  }
};

/**
 * Delete an organization with cascading deletion of all related data
 * @param {number} orgId - The organization ID
 * @returns {Promise<Object>} Promise resolving to the deletion result
 */
export const deleteOrganization = async (orgId) => {
  try {
    const response = await apiClient.delete(`/organizations/${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
};
